import { Tabs } from 'antd';
import { TableActionHeader } from 'components/Global';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	getCustomRulesAction,
	getDeleteCustomRulesAction,
	getEditCustomRulesAction,
	getSplitCustomRulesAction,
} from 'redux/action/customRuleAction';
import { AppDispatch } from 'redux/store';
import { checkPermission } from 'utils/utils';
import AddRuleModal from './AddRuleModal';
import DeleteRules from './DeleteRules';
import EditRules from './EditRules';
import SplitRules from './SplitRules';
import styles from './index.module.scss';
import { AddSvg } from 'utils/svgs';
import {
	CHAT_TIME_RULE_EVENT,
	TIME_RULE_TAB_KEY,
	buildTimeRulePrefill,
	consumeChatTimeRule,
	peekChatTimeRule,
} from 'utils/pendingChatTimeRule';

const CustomRules = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalData, setModalData] = useState(null);
	const [selectedRules, setSelectedRules] = useState('1');

	const [isCopy, setIsCopy] = useState<boolean | null | undefined>(false);

	// All three type-specific lists, so an edit request from the chat can find
	// its rule whichever tab is currently loaded. The slice keeps them separate
	// (splitData / editData / deleteData) because each tab fetches its own.
	const { splitData, editData, deleteData } = useSelector((state: any) => state?.customRules);

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	// for check is there add permission
	const isAddRulePermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Custom Rules',
		permission: ['add'],
	});

	const dispatch = useDispatch<AppDispatch>();

	const getCustomRulesData = async (triggerProcess: string) => {
		const query: any = {
			search: '',
		};

		switch (triggerProcess) {
			case 'split':
				await dispatch(getSplitCustomRulesAction(query));
				setSelectedRules('1');
				break;
			case 'edit':
				await dispatch(getEditCustomRulesAction(query));
				setSelectedRules('2');
				break;
			case 'delete':
				await dispatch(getDeleteCustomRulesAction(query));
				setSelectedRules('3');
				break;
			default:
				await dispatch(getCustomRulesAction(query));
		}
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setModalData(null);
	};

	const handleOpen = (data?: any, _isCopy?: boolean) => {
		setIsModalOpen(true);
		setModalData(data);
		setIsCopy(_isCopy);
	};

	// Opens this screen's own Add Rule modal on behalf of the AI chat, which
	// collects the rule's name, description and type and then hands the rest
	// over rather than rebuilding the criteria and split/edit pickers in a chat
	// column. See utils/pendingChatTimeRule.
	//
	// The tab is switched first and deliberately: AddRuleModal takes its
	// triggerProcess from modalKey — the open tab — not from its own form, so
	// opening on the wrong tab would create the wrong kind of rule. Both state
	// updates land in the same render, so the modal mounts with the new key.
	const openFromChatRequest = () => {
		const request = peekChatTimeRule();
		if (!request) {
			return;
		}

		if (request.ruleId) {
			const allRules = [...(splitData || []), ...(editData || []), ...(deleteData || [])];
			const existing = allRules.find((rule: any) => rule?.id === request.ruleId);
			if (!existing) {
				// The list for that type has not loaded yet. Leave the request
				// pending; the effect re-runs as each list arrives.
				return;
			}
			consumeChatTimeRule();
			const tabKey = TIME_RULE_TAB_KEY[existing.triggerProcess as 'split' | 'edit' | 'delete'];
			if (tabKey) {
				setSelectedRules(tabKey);
			}
			handleOpen(existing, false);
			return;
		}

		consumeChatTimeRule();
		if (request.ruleType) {
			setSelectedRules(TIME_RULE_TAB_KEY[request.ruleType]);
		}
		handleOpen(buildTimeRulePrefill(request), true);
	};

	useEffect(() => {
		// Mount-time read as well as the event: navigation can mount this panel
		// after requestChatTimeRule has already dispatched.
		openFromChatRequest();
		window.addEventListener(CHAT_TIME_RULE_EVENT, openFromChatRequest);
		return () => window.removeEventListener(CHAT_TIME_RULE_EVENT, openFromChatRequest);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [splitData, editData, deleteData]);

	const items = [
		{
			key: '1',
			label: 'Split Rules',
			children: (
				<SplitRules
					handleOpen={handleOpen}
					modalData={modalData}
				/>
			),
		},
		{
			key: '2',
			label: 'Edit Rules',
			children: (
				<EditRules
					handleOpen={handleOpen}
					modalData={modalData}
				/>
			),
		},
		{
			key: '3',
			label: 'Delete Rules',
			children: (
				<DeleteRules
					handleOpen={handleOpen}
					modalData={modalData}
				/>
			),
		},
	];

	return (
		<div>
			<TableActionHeader title={'Time Rules'}>
				{isAddRulePermission && (
					<button
						className={styles['add-rule-button']}
						onClick={() => handleOpen(null)}
					>
						<AddSvg />
						<p>Add Rule</p>
					</button>
				)}
			</TableActionHeader>

			<div className={styles['custom__rules-tabs']}>
				<Tabs
					defaultActiveKey="1"
					items={items}
					tabBarStyle={{
						paddingLeft: '20px',
						fontSize: '18px',
					}}
					activeKey={selectedRules}
					onChange={(e) => {
						// setModalKey(e === '1' ? 'split' : e === '2' ? 'edit' : 'delete');
						setSelectedRules(e);
					}}
				/>
			</div>
			{isModalOpen && (
				<AddRuleModal
					handleCloseModal={handleCloseModal}
					isModalOpen={isModalOpen}
					modalData={modalData}
					getCustomRulesData={getCustomRulesData}
					isCopy={isCopy}
					modalKey={selectedRules}
				/>
			)}
		</div>
	);
};

export default CustomRules;
